import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  api,
  type TournamentFormat,
  type TournamentStatus,
  type TournamentSummary,
} from '../api';
import { AppShell } from '../components/AppShell';

const FORMAT_LABELS: Record<TournamentFormat, string> = {
  SINGLE_ELIMINATION: 'Single elim',
  DOUBLE_ELIMINATION: 'Double elim (coming soon)',
  ROUND_ROBIN: 'Round robin',
  GROUP_BRACKET: 'Group + bracket (coming soon)',
};

const STATUS_STYLES: Record<TournamentStatus, string> = {
  DRAFT: 'border-white/10 bg-white/5 text-muted',
  SEEDING: 'border-amber-400/30 bg-amber-400/10 text-amber-300',
  LIVE: 'border-emerald-400/30 bg-emerald-400/10 text-emerald-300',
  COMPLETED: 'border-white/10 bg-white/5 text-muted',
};

export function TournamentsPage() {
  const [tournaments, setTournaments] = useState<TournamentSummary[]>([]);
  const [sports, setSports] = useState<
    Array<{ id: string; name: string; slug: string }>
  >([]);
  const [err, setErr] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<{
    name: string;
    sportId: string;
    format: TournamentFormat;
  }>({ name: '', sportId: '', format: 'SINGLE_ELIMINATION' });

  async function load() {
    try {
      setErr(null);
      const [t, s] = await Promise.all([
        api.listTournaments(),
        api.listSports(),
      ]);
      setTournaments(t);
      setSports(s);
      if (!form.sportId && s.length > 0) {
        setForm((f) => ({ ...f, sportId: s[0].id }));
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed_to_load');
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim() || !form.sportId) return;
    try {
      await api.createTournament({
        name: form.name.trim(),
        sportId: form.sportId,
        format: form.format,
      });
      setCreating(false);
      setForm({ ...form, name: '' });
      await load();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'create_failed');
    }
  }

  return (
    <AppShell>
      <div className="space-y-6">
        <div className="flex items-end justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Tournaments</h1>
            <p className="mt-1 text-muted">
              Brackets and round-robins across your sports.
            </p>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => setCreating((c) => !c)}
          >
            {creating ? 'Cancel' : '+ New tournament'}
          </button>
        </div>

        {creating && (
          <form className="card space-y-4" onSubmit={submit}>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <label className="block">
                <span className="mb-1 block text-xs uppercase tracking-wider text-muted">
                  Name
                </span>
                <input
                  className="input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs uppercase tracking-wider text-muted">
                  Sport
                </span>
                <select
                  className="input"
                  value={form.sportId}
                  onChange={(e) => setForm({ ...form, sportId: e.target.value })}
                >
                  {sports.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="mb-1 block text-xs uppercase tracking-wider text-muted">
                  Format
                </span>
                <select
                  className="input"
                  value={form.format}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      format: e.target.value as TournamentFormat,
                    })
                  }
                >
                  <option value="SINGLE_ELIMINATION">Single elimination</option>
                  <option value="ROUND_ROBIN">Round robin</option>
                  <option value="DOUBLE_ELIMINATION" disabled>
                    Double elimination (coming soon)
                  </option>
                  <option value="GROUP_BRACKET" disabled>
                    Group + bracket (coming soon)
                  </option>
                </select>
              </label>
            </div>
            <button type="submit" className="btn btn-primary">
              Create
            </button>
          </form>
        )}

        {err && <p className="card text-red-300">{err}</p>}

        {tournaments.length === 0 ? (
          <p className="card text-muted">
            No tournaments yet. Create one to organize a bracket or league.
          </p>
        ) : (
          <div className="card overflow-hidden p-0">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-xs uppercase tracking-wider text-muted">
                <tr>
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-left">Sport</th>
                  <th className="px-4 py-3 text-left">Format</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-right">Teams</th>
                  <th className="px-4 py-3 text-right">Matches</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {tournaments.map((t) => (
                  <tr key={t.id} className="hover:bg-white/5">
                    <td className="px-4 py-3">
                      <Link
                        to={`/tournaments/${t.id}`}
                        className="font-semibold text-text hover:text-brand"
                      >
                        {t.name}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-muted">{t.sport.name}</td>
                    <td className="px-4 py-3 text-muted">
                      {FORMAT_LABELS[t.format]}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLES[t.status]}`}
                      >
                        {t.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      {t._count.entries}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      {t._count.matches}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppShell>
  );
}
